/**
 * Function to calculate nice axis steps, for example 0, 10, 20, 30, or 0, 5, 10, 15
 * if it's not possible to calculate nice steps with numberOfSteps passed as param,
 * function will return close but different number of steps
 */
interface CalculateNiceAxisStepsParams {
  maxValue: number
  minValue?: number
  shouldUseExactMaxValue?: boolean
  numberOfSteps?: number
}

export const calculateNiceAxisSteps = ({
  maxValue,
  minValue = 0,
  numberOfSteps = 10,
  shouldUseExactMaxValue = false
}: CalculateNiceAxisStepsParams): {steps: number[]; stepSize: number} => {
  const span = maxValue - minValue
  // handling case here when maxValue is equal minValue
  if (span === 0) {
    return {steps: [maxValue], stepSize: 0}
  }
  // calculating matching power of ten step depending on span and number of steps
  // like 10, 100, 1000, 0.1, 0.01
  const powerOfTenStep: number = Math.pow(
    10,
    Math.floor(Math.log(span / numberOfSteps) / Math.LN10)
  )
  // calculating exact step value
  const nonRoundedStep = span / numberOfSteps
  // caculationg ration between beautiful power of ten step and exact non rounded step
  const ratio = powerOfTenStep / nonRoundedStep

  // Correcting step depending on ratio
  let correctedStep: number = powerOfTenStep
  if (ratio <= 0.15) correctedStep *= 10
  else if (ratio <= 0.35) correctedStep *= 5
  else if (ratio <= 0.75) correctedStep *= 2

  const minValueRounded = Math.ceil(minValue / correctedStep) * correctedStep
  const maxValueRounded = Math.ceil(maxValue / correctedStep) * correctedStep

  const steps: number[] = []

  for (let stepValue = minValueRounded; stepValue < maxValueRounded; stepValue += correctedStep) {
    steps.push(stepValue)
  }
  if (shouldUseExactMaxValue) {
    const shouldRemoveStepBeforMaxValue: boolean =
      maxValue - steps[steps.length - 1] <= correctedStep
    if (shouldRemoveStepBeforMaxValue) {
      steps.pop()
    }
    steps.push(maxValue)
  } else {
    steps.push(steps[steps.length - 1] + correctedStep)
  }

  return {steps, stepSize: correctedStep}
}
